import { TICKET_MIN_PAID_PRICE_CENTS, ticketOrderAmounts } from '@pulsify/shared'
import type { TicketSettings, TicketType } from '@pulsify/shared/dto'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { toast } from 'sonner'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Switch } from '~/shared/ui/switch'
import { formatCents } from './ticket-format'
import {
  draftFromType,
  draftPriceCents,
  inputFromDraft,
  type TicketTypeDraft,
  type TicketTypeIssue,
} from './ticket-type-form'

function issueMessage(issue: TicketTypeIssue, soldCount: number): string {
  switch (issue) {
    case 'name':
      return m.ticketsIssueName()
    case 'price':
      return m.ticketsIssuePrice()
    case 'priceTooLow':
      return m.ticketsIssuePriceTooLow({ min: formatCents(TICKET_MIN_PAID_PRICE_CENTS) })
    case 'seats':
      return m.ticketsIssueSeats()
    case 'seatsBelowSold':
      return m.ticketsIssueSeatsBelowSold({ sold: String(soldCount) })
    case 'doorPrice':
      return m.ticketsIssueDoorPrice()
  }
}

export function TicketTypeDialog({
  open,
  onOpenChange,
  type,
  settings,
  onSave,
  draft = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: TicketType | null
  settings: TicketSettings
  onSave: (input: TicketTypeInput) => Promise<unknown>
  draft?: boolean
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{type ? m.ticketsEditType() : m.ticketsNewType()}</DialogTitle>
        </DialogHeader>
        <TicketTypeForm
          key={type?.id ?? 'new'}
          type={type}
          settings={settings}
          onSave={onSave}
          editsSaved={type !== null && !draft}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

function TicketTypeForm({
  type,
  settings,
  onSave,
  editsSaved,
  onDone,
}: {
  type: TicketType | null
  settings: TicketSettings
  onSave: (input: TicketTypeInput) => Promise<unknown>
  editsSaved: boolean
  onDone: () => void
}) {
  const id = useId()
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(() => draftFromType(type))
  const [issue, setIssue] = useState<TicketTypeIssue | null>(null)
  const soldCount = type?.soldCount ?? 0
  const priceCents = draftPriceCents(draft)
  const paid = priceCents > 0

  const update = (patch: Partial<TicketTypeDraft>) => {
    setDraft((current) => ({ ...current, ...patch }))
    setIssue(null)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const result = inputFromDraft(draft, soldCount)
    if (!result.ok) {
      setIssue(result.issue)
      return
    }
    setSaving(true)
    try {
      await onSave(result.input)
      onDone()
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-name`}>{m.ticketsFieldName()}</Label>
        <Input
          id={`${id}-name`}
          value={draft.name}
          maxLength={80}
          placeholder={m.ticketsFieldNamePlaceholder()}
          aria-invalid={issue === 'name'}
          onChange={(event) => update({ name: event.target.value })}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-price`}>{m.ticketsFieldPrice()}</Label>
          <Input
            id={`${id}-price`}
            inputMode="decimal"
            autoComplete="off"
            value={draft.price}
            aria-invalid={issue === 'price' || issue === 'priceTooLow'}
            aria-describedby={`${id}-price-hint`}
            onChange={(event) => update({ price: event.target.value })}
          />
          <p id={`${id}-price-hint`} className="text-muted-foreground text-xs">
            {m.ticketsFieldPriceHint()}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-seats`}>{m.ticketsFieldSeats()}</Label>
          <Input
            id={`${id}-seats`}
            type="number"
            min={Math.max(soldCount, 1)}
            inputMode="numeric"
            placeholder={m.ticketsFieldSeatsPlaceholder()}
            value={draft.seats}
            aria-invalid={issue === 'seats' || issue === 'seatsBelowSold'}
            onChange={(event) => update({ seats: event.target.value })}
          />
        </div>
      </div>

      {paid ? (
        <FeePreview priceCents={priceCents} settings={settings} />
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-door`}>{m.ticketsFieldDoorPrice()}</Label>
          <Input
            id={`${id}-door`}
            inputMode="decimal"
            autoComplete="off"
            className="sm:w-1/2"
            value={draft.doorPrice}
            aria-invalid={issue === 'doorPrice'}
            aria-describedby={`${id}-door-hint`}
            onChange={(event) => update({ doorPrice: event.target.value })}
          />
          <p id={`${id}-door-hint`} className="text-muted-foreground text-xs">
            {m.ticketsFieldDoorPriceHint()}
          </p>
        </div>
      )}

      <SwitchRow
        id={`${id}-student`}
        label={m.ticketsFieldStudent()}
        hint={m.ticketsFieldStudentHint()}
        checked={draft.isStudent}
        onChange={(isStudent) => update({ isStudent })}
      />
      {editsSaved ? (
        <SwitchRow
          id={`${id}-active`}
          label={m.ticketsFieldActive()}
          hint={m.ticketsFieldActiveHint()}
          checked={draft.active}
          onChange={(active) => update({ active })}
        />
      ) : null}

      {issue ? (
        <p role="alert" className="text-destructive text-sm">
          {issueMessage(issue, soldCount)}
        </p>
      ) : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          {m.cancel()}
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? m.saving() : m.save()}
        </Button>
      </DialogFooter>
    </form>
  )
}

function FeePreview({ priceCents, settings }: { priceCents: number; settings: TicketSettings }) {
  const amounts = ticketOrderAmounts({
    priceCents,
    quantity: 1,
    tier: settings.tier,
    feeMode: settings.feeMode,
  })
  const fee = formatCents(amounts.feeCents)
  return (
    <div className="space-y-1 text-muted-foreground text-sm">
      <p>
        {settings.feeMode === 'buyer'
          ? m.ticketsFeePreviewBuyer({ total: formatCents(amounts.totalCents), fee })
          : m.ticketsFeePreviewOrganizer({ net: formatCents(amounts.organizerNetCents), fee })}
      </p>
      {settings.connect.onboardingComplete ? null : (
        <p>{settings.connect.available ? m.ticketsPaidNeedsPayouts() : m.ticketsPaidSoon()}</p>
      )}
    </div>
  )
}

function SwitchRow({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string
  label: ReactNode
  hint: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-muted-foreground text-sm">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}
